import { connect } from 'react-redux';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { parse } from '../../util/urlHelpers';
import { getBlogPageContent, saveBlogPageContent } from '../../util/api';
import { LayoutSingleColumn, Page, Modal, Button, PrimaryButton, SecondaryButton } from '../../components';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import FooterComponent from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import Template1Editorial from './templates/Template1Editorial';
import Template2BoldSplit from './templates/Template2BoldSplit';
import Template3Minimal from './templates/Template3Minimal';
import css from './FortehBlogPage.module.css';
import { useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useEffect, useState } from 'react';
import { useConfiguration } from '../../context/configurationContext';
import EditableRichField from './EditableRichField';
import { stripHtml } from './blogHtml';

// ─── Initial Data ────────────────────────────────────────────────────────────

const initialPageData = {
  hero: {
    label: 'The Forteh Blog',
    title: 'What Is Fractional Work — And Why Is Everyone Talking About It?',
    subtext: "The world of work has changed. Fractional isn't a trend. It's the movement that was always coming.",
    meta: ['📍 Published by Forteh', '⏱ 6 min read', '🇨🇦 Canada'],
  },
  steps: [
    {
      num: 1,
      label: 'Part-time commitment',
      text: 'Usually 1–3 days per week, or a defined number of hours per month — structured around what the company actually needs.',
    },
    {
      num: 2,
      label: 'Contract-based',
      text: 'The professional works as a contractor or through their own business — not as a permanent employee. No benefits overhead, no severance risk.',
    },
    {
      num: 3,
      label: 'Ongoing relationship',
      text: "These aren't one-off gigs. Engagements typically run 3–12 months, often renewed. The fractional professional becomes a trusted part of the team.",
    },
    {
      num: 4,
      label: 'Remote-first',
      text: 'Fractionals work largely remotely, with occasional on-site presence when needed — giving companies access to talent beyond their postal code.',
    },
  ],
  roles: [
    'Fractional CFO', 'Fractional CMO', 'Fractional COO', 'Fractional CHRO',
    'Fractional CTO', 'HR Professional', 'Payroll Specialist', 'Health & Safety',
    'Marketing & Comms', 'Legal Counsel', 'Sales Leader', 'UX / Product',
    'Software Engineer', 'AI Specialist', 'ESG Reporting', 'Operations',
    'Procurement', 'Board Director',
  ],
  tableRows: [
    { arrangement: 'Full-Time Employee', desc: '40 hrs/week, full overhead, benefits, severance risk, long-term commitment' },
    { arrangement: 'Consultant', desc: 'Project-specific, arms-length, delivers recommendations but often not execution' },
    { arrangement: 'Freelancer', desc: 'Task-based, execution-focused, lower seniority, typically transactional' },
    { arrangement: 'Interim', desc: 'Temporary fill-in while searching for a permanent hire — a bridge, not a destination' },
    { arrangement: 'Fractional ✓', desc: 'Senior expertise, embedded in your team, ongoing relationship, fraction of the cost', highlight: true },
  ],
  stats: [
    { num: '20%', label: 'Lower cost vs. full-time equivalent roles on average' },
    { num: '3–12', label: 'Months — typical engagement length, often renewed' },
    { num: '1–3', label: 'Days per week — the most common fractional commitment' },
    { num: '↑', label: "Canada's fractional adoption is accelerating fast" },
  ],
  article: {
    p1: "Let's start with an uncomfortable truth: the way most companies hire hasn't changed in decades — but the way the best professionals want to work has changed dramatically. Fractional work is what happens when those two realities finally collide.",
    p2: 'You\'ve probably heard the term. Maybe a colleague mentioned their "fractional CFO." Maybe you\'ve seen it on LinkedIn and assumed it was just a fancy word for freelance. Or maybe you\'re a seasoned professional wondering if this model is actually for someone like you.',
    p3: 'Spoiler: it is. And it might be the most important shift in Canadian work culture since remote work went mainstream.',
    quote1: "Fractional isn't a title. It's a work model — and it was built for everyone who has skills worth sharing, not just the ones with 'Chief' in their name.",
    h2_1: 'So, What Exactly Is Fractional?',
    p4: "A fractional professional is an experienced specialist — often someone who would otherwise be a full-time employee — who works with multiple companies simultaneously, dedicating a fraction of their time to each. Think of it as having access to exactly the expertise you need, without paying for 40 hours a week you don't.",
    p5: 'A typical fractional engagement looks like this:',
    h2_2: 'The Myth We Need to Kill',
    p6: 'When most people hear "fractional," they picture a Fractional CFO for a Series A startup. That\'s where it started — and the executives got all the headlines. But here\'s what nobody talks about loudly enough:',
    highlight1: "Fractional work is not just for C-suite executives. It's for anyone with specialized skills a company needs — but doesn't need full-time.",
    p7: "A payroll specialist with 12 years of experience. An HR professional who's built culture from the ground up. A health & safety coordinator keeping worksites compliant. A UX designer who builds products people actually use. A project manager who can run sprints in their sleep.",
    p8: 'These professionals are just as valuable as the person with "Chief" in their title. Companies need them just as much. They just don\'t need them 40 hours a week. And that\'s exactly where fractional comes in.',
    h3_1: 'Fractional Roles at Forteh',
    h2_3: 'How Is It Different From Consulting or Freelance?',
    p9: "Good question — and it matters. These terms get used interchangeably, but they're not the same thing.",
    h2_4: 'Why Did This Happen Now?',
    p10: 'Fractional work wasn\'t invented in a boardroom. It was born from necessity — from professionals who were laid off, downsized, or pushed out of corporate life and discovered something unexpected: offering their skills part-time to multiple companies worked better than one job ever had.',
    p11: 'Necessity became a strategy. Strategy became a movement.',
    p12: 'Several forces collided to accelerate this:',
    p13_strong: 'For professionals:',
    p13_text: 'A generation of senior talent realized that one salary is no longer job security. Economic uncertainty, corporate restructuring, and the desire for autonomy pushed skilled people toward portfolio careers — multiple clients, multiple income streams, more control.',
    p14_strong: 'For companies:',
    p14_text: 'Post-pandemic economics forced leaner thinking. Why carry a $300K fully-loaded executive for a challenge that needs 2 days a week of their time? The math on fractional is hard to argue with.',
    p15_strong: 'For Canada specifically:',
    p15_text: 'Remote work normalized working with talent outside your city. Fractional is the next evolution — access to the right person, at the right time, wherever they are in the country.',
    h2_5: 'Is Fractional Right for You?',
    h3_2: "If You're a Company",
    p16: "You're ready for fractional if you need senior expertise but can't justify — or don't need — a full-time hire. If you're scaling, restructuring, launching something new, or navigating a gap in your leadership team, a fractional professional can move fast, integrate into your team, and deliver without the overhead of a permanent hire.",
    h3_3: "If You're a Professional",
    p17: "You're ready for fractional if you've built real expertise and want more than one company to benefit from it. If you're craving flexibility, tired of tying your livelihood to a single employer, or want to work across industries — fractional gives you the autonomy of running your own business with the depth of an embedded team member.",
    quote2: '"One salary is no longer job security. The professionals who figured that out early are already building portfolio careers. The rest are catching up."',
    h2_6: 'Where Forteh Comes In',
    p18: 'Forteh is Canada\'s marketplace for fractional talent — built to connect companies with the specialized professionals they need, and to help skilled professionals find companies that align with their values and schedule.',
    p19: "We're not a traditional recruiter. We're not a staffing agency. We're a platform built specifically for this new world of work — one where the best talent doesn't have to choose between doing great work and living a great life.",
    p20: "Whether you're a company looking to hire smarter, or a professional ready to take control of your career — this is where it starts.",
  },
  cta: {
    title: 'Ready to Experience Fractional?',
    sub: "Join the movement that's changing how Canada works — on both sides of the table.",
    btn1: 'Hire Fractional Talent',
    btn1Url: 'https://forteh-development-546ec4c7069f.herokuapp.com/',
    btn2: 'Join as a Professional',
    btn2Url: 'https://forteh-development-546ec4c7069f.herokuapp.com/',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Check if a value has meaningful content (not null, undefined, empty, or whitespace-only)
const hasValue = (val) => val != null && typeof val === 'string' && val.trim().length > 0;


// ─── Sub-components ──────────────────────────────────────────────────────────

const EditableField = props => <EditableRichField {...props} css={css} />;

const DeleteButton = ({ onClick, title = 'Delete Section' }) => (
  <button className={css.deleteSectionBtn} onClick={onClick} title={title}>
    ×
  </button>
);

const EditWrapper = ({ children, isEditMode, onDelete, className }) => {
  if (!isEditMode) return children;
  return (
    <div className={classNames(css.editWrapper, className)}>
      {onDelete && <DeleteButton onClick={onDelete} />}
      {children}
    </div>
  );
};

const EditableButton = ({ title, url, onTitleChange, onUrlChange, isEditMode, className, placeholderTitle, placeholderUrl }) => {
  if (isEditMode) {
    return (
      <div className={css.buttonEditContainer}>
        <input
          className={css.editableInput}
          type="text"
          value={title || ''}
          onChange={e => onTitleChange(e.target.value)}
          placeholder={placeholderTitle || 'Button Title'}
        />
        <input
          className={css.editableInput}
          type="text"
          value={url || ''}
          onChange={e => onUrlChange(e.target.value)}
          placeholder={placeholderUrl || 'https://'}
        />
      </div>
    );
  }
  if (!title) return null;
  return (
    <a className={className} href={url || '#'} target="_blank" rel="noopener noreferrer">
      {title}
    </a>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => (
  <Modal
    id="FortehBlogDeleteConfirmation"
    isOpen={isOpen}
    onClose={onClose}
    onManageDisableScrolling={() => { }}
    usePortal
  >
    <div className={css.modalContent}>
      <h3 className={css.modalTitle}>{title || 'Are you sure?'}</h3>
      <p className={css.modalMessage}>{message || 'This action cannot be undone.'}</p>
      <div className={css.modalActions}>
        <SecondaryButton onClick={onClose} className={css.modalBtn}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className={classNames(css.modalBtn, css.modalConfirmBtn)}
        >
          Confirm Delete
        </PrimaryButton>
      </div>
    </div>
  </Modal>
);

function Hero({ data = {}, onChange, isEditMode, triggerConfirm }) {
  const handleDelete = () => {
    triggerConfirm(
      'Clear Hero Section?',
      'Are you sure you want to clear all text and metadata in the hero section?',
      () => onChange('hero', { label: '', title: '', subtext: '', meta: ['', '', ''] })
    );
  };

  return (
    <section className={css.hero}>
      <EditWrapper isEditMode={isEditMode} onDelete={handleDelete}>
        {(isEditMode || hasValue(data?.label)) && (
          <div className={`${css.heroLabel} ${css.anim1}`}>
            <EditableField
              tag="span"
              value={data?.label}
              onChange={val => onChange('hero', { ...data, label: val })}
              isEditMode={isEditMode}
            />
          </div>
        )}
        {(isEditMode || hasValue(data?.title)) && (
          <h1 className={css.anim1}>
            <EditableField
              tag="span"
              value={data?.title}
              onChange={val => onChange('hero', { ...data, title: val })}
              isEditMode={isEditMode}
              multiline
            />
          </h1>
        )}
        {(isEditMode || hasValue(data?.subtext)) && (
          <div className={`${css.heroSub} ${css.anim2}`}>
            <EditableField
              tag="p"
              value={data?.subtext}
              onChange={val => onChange('hero', { ...data, subtext: val })}
              isEditMode={isEditMode}
              multiline
            />
          </div>
        )}
        {(isEditMode || (data?.meta && data?.meta.some(m => hasValue(m)))) && (
          <div className={`${css.heroMeta} ${css.anim3}`}>
            {(data?.meta || []).map((m, i) => (
              (isEditMode || hasValue(m)) ? (
                <span key={i}>
                  <EditableField
                    tag="span"
                    value={m}
                    onChange={val => {
                      const newMeta = [...(data?.meta || [])];
                      newMeta[i] = val;
                      onChange('hero', { ...data, meta: newMeta });
                    }}
                    isEditMode={isEditMode}
                  />
                </span>
              ) : null
            ))}
          </div>
        )}
      </EditWrapper>
    </section>
  );
}

function Steps({ items = [], onChange, isEditMode, triggerConfirm }) {
  const visibleItems = isEditMode ? items : items.filter(s => hasValue(s?.label) || hasValue(s?.text));
  if (!isEditMode && visibleItems.length === 0) return null;

  const handleDeleteSection = () => {
    triggerConfirm(
      'Delete Steps?',
      'Are you sure you want to remove the entire "Steps" section?',
      () => onChange('steps', [])
    );
  };

  return (
    <div className={css.steps}>
      <EditWrapper isEditMode={isEditMode} onDelete={handleDeleteSection}>
        {items.map((s, i) => {
          if (!isEditMode && !hasValue(s?.label) && !hasValue(s?.text)) return null;
          return (
            <EditWrapper
              key={i}
              isEditMode={isEditMode}
              onDelete={() => triggerConfirm('Delete Step?', `Remove step ${s?.num}?`, () => {
                const newItems = items.filter((_, idx) => idx !== i);
                onChange('steps', newItems);
              })}
            >
              <div className={css.step}>
                <div className={css.stepNum}>{s?.num}</div>
                <div className={css.stepText}>
                  <EditableField
                    tag="strong"
                    value={s?.label}
                    onChange={val => {
                      const newItems = [...items];
                      newItems[i] = { ...newItems[i], label: val };
                      onChange('steps', newItems);
                    }}
                    isEditMode={isEditMode}
                  />
                  {(isEditMode || (hasValue(s?.label) && hasValue(s?.text))) && ': '}
                  <EditableField
                    tag="span"
                    value={s?.text}
                    onChange={val => {
                      const newItems = [...items];
                      newItems[i] = { ...newItems[i], text: val };
                      onChange('steps', newItems);
                    }}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              </div>
            </EditWrapper>
          );
        })}
        {isEditMode && (
          <button className={css.addSectionBtn} onClick={() => onChange('steps', [...items, { num: items.length + 1, label: 'New Step', text: '' }])}>
            + Add Step
          </button>
        )}
      </EditWrapper>
    </div>
  );
}

function RolesGrid({ items = [], onChange, isEditMode, triggerConfirm }) {
  const visibleItems = isEditMode ? items : items.filter(role => hasValue(role));
  if (!isEditMode && visibleItems.length === 0) return null;

  const handleDeleteSection = () => {
    triggerConfirm(
      'Delete Roles Grid?',
      'Are you sure you want to remove all roles from this section?',
      () => onChange('roles', [])
    );
  };

  return (
    <div className={css.rolesGrid}>
      <EditWrapper isEditMode={isEditMode} onDelete={handleDeleteSection} className={css.rolesGrid}>
        {items.map((role, i) => {
          if (!isEditMode && !hasValue(role)) return null;
          return (
            <div key={i} className={css.roleChip} style={{ position: 'relative' }}>
              <EditableField
                tag="span"
                value={role}
                onChange={val => {
                  const newItems = [...items];
                  newItems[i] = val;
                  onChange('roles', newItems);
                }}
                isEditMode={isEditMode}
              />
              {isEditMode && (
                <button
                  className={css.deleteSectionBtn}
                  style={{ top: '-5px', right: '-5px', width: '18px', height: '18px', fontSize: '12px' }}
                  onClick={() => triggerConfirm('Delete Role?', `Remove role "${role}"?`, () => onChange('roles', items.filter((_, idx) => idx !== i)))}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        {isEditMode && (
          <button
            className={classNames(css.roleChip, css.addChip)}
            onClick={() => onChange('roles', [...items, 'New Role'])}
            style={{ cursor: 'pointer', border: '1px dashed var(--terra)', background: 'transparent' }}
          >
            + Add Role
          </button>
        )}
      </EditWrapper>
    </div>
  );
}

function CompareTable({ rows = [], onChange, isEditMode, triggerConfirm }) {
  const visibleRows = isEditMode ? rows : rows.filter(row => hasValue(row?.arrangement) || hasValue(row?.desc));
  if (!isEditMode && visibleRows.length === 0) return null;

  const handleDeleteSection = () => {
    triggerConfirm(
      'Delete Comparison Table?',
      'Are you sure you want to remove the entire comparison table?',
      () => onChange('tableRows', [])
    );
  };

  return (
    <EditWrapper isEditMode={isEditMode} onDelete={handleDeleteSection}>
      <table className={css.compareTable}>
        <thead>
          <tr>
            <th>Arrangement</th>
            <th>What It Looks Like</th>
            {isEditMode && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            if (!isEditMode && !hasValue(row?.arrangement) && !hasValue(row?.desc)) return null;
            return (
              <tr key={i}>
                <td style={row?.highlight ? { fontWeight: 700 } : {}}>
                  <EditableField
                    tag="span"
                    value={row?.arrangement}
                    onChange={val => {
                      const newRows = [...rows];
                      newRows[i] = { ...newRows[i], arrangement: val };
                      onChange('tableRows', newRows);
                    }}
                    isEditMode={isEditMode}
                  />
                </td>
                <td>
                  <EditableField
                    tag="span"
                    value={row?.desc}
                    onChange={val => {
                      const newRows = [...rows];
                      newRows[i] = { ...newRows[i], desc: val };
                      onChange('tableRows', newRows);
                    }}
                    isEditMode={isEditMode}
                    multiline
                  />
                </td>
                {isEditMode && (
                  <td>
                    <button
                      className={css.deleteSectionBtn}
                      style={{ position: 'static', opacity: 1 }}
                      onClick={() => triggerConfirm('Delete Row?', `Remove row "${row?.arrangement}"?`, () => onChange('tableRows', rows.filter((_, idx) => idx !== i)))}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {isEditMode && (
        <button className={css.addSectionBtn} onClick={() => onChange('tableRows', [...rows, { arrangement: 'New Row', desc: '' }])}>
          + Add Table Row
        </button>
      )}
    </EditWrapper>
  );
}

function EngagementBox({ items = [], onChange, isEditMode, triggerConfirm }) {
  const visibleItems = isEditMode ? items : items.filter(s => hasValue(s?.num) || hasValue(s?.label));
  if (!isEditMode && visibleItems.length === 0) return null;

  const handleDeleteSection = () => {
    triggerConfirm(
      'Delete Engagement Stats?',
      'Are you sure you want to remove the entire engagement stats section?',
      () => onChange('stats', [])
    );
  };

  return (
    <EditWrapper isEditMode={isEditMode} onDelete={handleDeleteSection}>
      <div className={css.engagementBox}>
        {items.map((s, i) => {
          if (!isEditMode && !hasValue(s?.num) && !hasValue(s?.label)) return null;
          return (
            <div key={i} className={css.engagementStat} style={{ position: 'relative' }}>
              {(isEditMode || hasValue(s?.num)) && (
                <div className={css.num}>
                  <EditableField
                    tag="span"
                    value={s?.num}
                    onChange={val => {
                      const newItems = [...items];
                      newItems[i] = { ...newItems[i], num: val };
                      onChange('stats', newItems);
                    }}
                    isEditMode={isEditMode}
                  />
                </div>
              )}
              {(isEditMode || hasValue(s?.label)) && (
                <div className={css.label}>
                  <EditableField
                    tag="span"
                    value={s?.label}
                    onChange={val => {
                      const newItems = [...items];
                      newItems[i] = { ...newItems[i], label: val };
                      onChange('stats', newItems);
                    }}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              )}
              {isEditMode && (
                <button
                  className={css.deleteSectionBtn}
                  style={{ top: '-10px', right: '-10px', width: '18px', height: '18px', fontSize: '12px' }}
                  onClick={() => triggerConfirm('Delete Stat?', `Remove stat "${s?.label}"?`, () => onChange('stats', items.filter((_, idx) => idx !== i)))}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>
      {isEditMode && (
        <button className={css.addSectionBtn} onClick={() => onChange('stats', [...items, { num: '0', label: 'New Stat' }])}>
          + Add Stat
        </button>
      )}
    </EditWrapper>
  );
}

function CtaBlock({ data = {}, onChange, isEditMode, triggerConfirm }) {
  const hasAnyContent = isEditMode || hasValue(data?.title) || hasValue(data?.sub) || hasValue(data?.btn1) || hasValue(data?.btn2);
  if (!hasAnyContent) return null;

  const handleDelete = () => {
    triggerConfirm(
      'Clear CTA Block?',
      'Are you sure you want to clear all titles and buttons in this CTA block?',
      () => onChange('cta', { title: '', sub: '', btn1: '', btn2: '' })
    );
  };

  return (
    <EditWrapper isEditMode={isEditMode} onDelete={handleDelete}>
      <div className={classNames(css.ctaBlock, isEditMode && css.ctaBlockEditMode)}>
        {(isEditMode || hasValue(data?.title)) && (
          <h2 style={{ border: 'none', padding: 0 }}>
            <EditableField
              tag="span"
              value={data?.title}
              onChange={val => onChange('cta', { ...data, title: val })}
              isEditMode={isEditMode}
            />
          </h2>
        )}
        {(isEditMode || hasValue(data?.sub)) && (
          <p>
            <EditableField
              tag="span"
              value={data?.sub}
              onChange={val => onChange('cta', { ...data, sub: val })}
              isEditMode={isEditMode}
              multiline
            />
          </p>
        )}
        {(isEditMode || hasValue(data?.btn1) || hasValue(data?.btn2)) && (
          <div className={css.ctaButtons}>
            {(isEditMode || hasValue(data?.btn1)) && (
              <EditableButton
                title={data?.btn1}
                url={data?.btn1Url}
                onTitleChange={val => onChange('cta', { ...data, btn1: val })}
                onUrlChange={val => onChange('cta', { ...data, btn1Url: val })}
                isEditMode={isEditMode}
                className={`${css.btn} ${css.btnTerra}`}
                placeholderTitle="Button 1 Label"
                placeholderUrl="https://"
              />
            )}
            {(isEditMode || hasValue(data?.btn2)) && (
              <EditableButton
                title={data?.btn2}
                url={data?.btn2Url}
                onTitleChange={val => onChange('cta', { ...data, btn2: val })}
                onUrlChange={val => onChange('cta', { ...data, btn2Url: val })}
                isEditMode={isEditMode}
                className={`${css.btn} ${css.btnOutline}`}
                placeholderTitle="Button 2 Label"
                placeholderUrl="https://"
              />
            )}
          </div>
        )}
      </div>
    </EditWrapper>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const FortehBlogPageComponent = (props) => {
  const {
    scrollingDisabled,
    currentUser,
    onManageDisableScrolling,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const { slug } = params;
  const config = useConfiguration();
  const query = parse(location.search);
  const isEditModeParam = !!query.edit;

  const isAdmin = currentUser?.attributes?.profile?.privateData?.admin;

  const [pageData, setPageData] = useState(initialPageData);
  const [templateId, setTemplateId] = useState('original');
  const [isEditMode, setIsEditMode] = useState(isEditModeParam);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('published'); // 'draft' or 'published'

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });

  const triggerConfirm = (title, message, onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchContent = async () => {
    console.log('Fetching content for slug:', slug);
    setLoading(true);
    try {
      // Check if we have a template from URL (new blog creation)
      const urlTemplate = query.template;
      if (urlTemplate) {
        setTemplateId(urlTemplate);
        // Load template data from sessionStorage if available
        const stored = sessionStorage.getItem('selectedTemplate');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed.id === urlTemplate && parsed.data) {
              setPageData(parsed.data);
            }
          } catch (e) {
            console.log('Failed to parse stored template');
          }
        }
      }

      if (isAdmin && slug) {
        console.log('Trying draft for:', slug);
        try {
          const res = await getBlogPageContent({ status: 'draft', slug });
          if (res.success && res.data?.pageData) {
            console.log('Draft found');
            setPageData(res.data.pageData);
            setTemplateId(res.data?.templateId || 'original');
            setCurrentStatus('draft');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.log('No draft found, continuing to published check.');
          // It's okay if draft is not found, we will check published next
        }
      }

      if (slug) {
        console.log('Trying published for:', slug);
        const res = await getBlogPageContent({ status: 'published', slug });
        if (res.success && res.data?.pageData) {
          console.log('Published found');
          setPageData(res.data.pageData);
          setTemplateId(res.data?.templateId || 'original');
          setCurrentStatus('published');
        } else {
          console.log('No published content found for slug, showing ERROR state');
          // If we have a slug but no content, do NOT show static default. 
          // Show a clear error or a blank state to reveal the 404.
          setPageData({
            ...initialPageData,
            hero: { ...initialPageData.hero, title: 'Error: Blog Not Found', subtext: `Slug: ${slug}` }
          });
          setCurrentStatus('not_found');
        }
      } else if (!urlTemplate) {
        console.log('No slug provided, showing initial content for new blog');
        setPageData(initialPageData);
        setCurrentStatus('new');
      }
    } catch (err) {
      console.error('Failed to fetch blog content:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [isAdmin, slug]);

  const handleUpdate = (key, value) => {
    setPageData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleUpdateArticle = (key, value) => {
    setPageData(prev => ({
      ...prev,
      article: {
        ...prev.article,
        [key]: value,
      },
    }));
  };

  const handleSave = async (status = 'draft') => {
    setSaving(true);
    try {
      const res = await saveBlogPageContent({ pageData, status, slug, templateId });
      toast.success(`Content saved as ${status}!`);

      // If we created a new blog or updated a published one, redirect to the list
      if (res.success) {
        history.push('/everything-fractional');
      }
    } catch (err) {
      toast.error('Failed to save content.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      // Save current editor state directly to 'published' status
      const res = await saveBlogPageContent({ pageData, status: 'published', slug, templateId });

      if (res.success) {
        toast.success('Live content updated successfully!');
        // Redirect to the blog list page
        history.push('/everything-fractional');
      }
    } catch (err) {
      toast.error('Failed to update live content.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn
          topbar={
            <TopbarContainer
              className={css.topbar}
              mobileRootClassName={css.mobileTopbar}
              desktopClassName={css.desktopTopbar}
              currentPage="FortehBlogPage"
            />
          }
          footer={<FooterComponent />}
          className={css.pageRoot}
        >
          <div className={css.loadingOverlay}>
            <div className={css.loadingContent}>
              <div className={css.spinner}></div>
              <div className={css.loadingText}>Loading Blog Content</div>
              <div className={css.loadingSubtext}>Just a moment...</div>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const art = pageData?.article || {};

  // Render the appropriate template based on templateId
  const renderTemplate = () => {
    const templateProps = {
      data: pageData,
      onChange: handleUpdate,
      isEditMode,
      triggerConfirm
    };

    switch (templateId) {
      case 'template1':
        return <Template1Editorial {...templateProps} />;
      case 'template2':
        return <Template2BoldSplit {...templateProps} />;
      case 'template3':
        return <Template3Minimal {...templateProps} />;
      case 'original':
      default:
        // Original FortehBlog template rendering
        return (
          <>
            <Hero data={pageData?.hero} onChange={handleUpdate} isEditMode={isEditMode} triggerConfirm={triggerConfirm} />
            <div className={css.articleWrap}>
              <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p1', ''))}>
                <EditableField
                  tag="p"
                  value={art?.p1}
                  onChange={val => handleUpdateArticle('p1', val)}
                  isEditMode={isEditMode}
                  multiline
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p2', ''))}>
              <EditableField
                tag="p"
                value={art?.p2}
                onChange={val => handleUpdateArticle('p2', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p3', ''))}>
              <EditableField
                tag="p"
                value={art?.p3}
                onChange={val => handleUpdateArticle('p3', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>

            {(isEditMode || hasValue(art?.quote1)) && (
              <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Quote?', 'Remove this pull quote?', () => handleUpdateArticle('quote1', ''))}>
                <div className={css.pullQuote}>
                  <EditableField
                    tag="p"
                    value={art?.quote1}
                    onChange={val => handleUpdateArticle('quote1', val)}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              </EditWrapper>
            )}

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Heading?', 'Remove this heading?', () => handleUpdateArticle('h2_1', ''))}>
              <EditableField
                tag="h2"
                value={art?.h2_1}
                onChange={val => handleUpdateArticle('h2_1', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p4', ''))}>
              <EditableField
                tag="p"
                value={art?.p4}
                onChange={val => handleUpdateArticle('p4', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p5', ''))}>
              <EditableField
                tag="p"
                value={art?.p5}
                onChange={val => handleUpdateArticle('p5', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>

            <Steps items={pageData?.steps} onChange={handleUpdate} isEditMode={isEditMode} triggerConfirm={triggerConfirm} />

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Heading?', 'Remove this heading?', () => handleUpdateArticle('h2_2', ''))}>
              <EditableField
                tag="h2"
                value={art?.h2_2}
                onChange={val => handleUpdateArticle('h2_2', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p6', ''))}>
              <EditableField
                tag="p"
                value={art?.p6}
                onChange={val => handleUpdateArticle('p6', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>

            {(isEditMode || hasValue(art?.highlight1)) && (
              <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Highlight?', 'Remove this highlighted block?', () => handleUpdateArticle('highlight1', ''))}>
                <div className={css.highlightStrip}>
                  <EditableField
                    tag="p"
                    value={art?.highlight1}
                    onChange={val => handleUpdateArticle('highlight1', val)}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              </EditWrapper>
            )}

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p7', ''))}>
              <EditableField
                tag="p"
                value={art?.p7}
                onChange={val => handleUpdateArticle('p7', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p8', ''))}>
              <EditableField
                tag="p"
                value={art?.p8}
                onChange={val => handleUpdateArticle('p8', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Sub-heading?', 'Remove this sub-heading?', () => handleUpdateArticle('h3_1', ''))}>
              <EditableField
                tag="p"
                className={css.h3}
                value={art?.h3_1}
                onChange={val => handleUpdateArticle('h3_1', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <RolesGrid items={pageData?.roles} onChange={handleUpdate} isEditMode={isEditMode} triggerConfirm={triggerConfirm} />

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Heading?', 'Remove this heading?', () => handleUpdateArticle('h2_3', ''))}>
              <EditableField
                tag="h2"
                value={art?.h2_3}
                onChange={val => handleUpdateArticle('h2_3', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p9', ''))}>
              <EditableField
                tag="p"
                value={art?.p9}
                onChange={val => handleUpdateArticle('p9', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>
            <CompareTable rows={pageData?.tableRows} onChange={handleUpdate} isEditMode={isEditMode} triggerConfirm={triggerConfirm} />

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Heading?', 'Remove this heading?', () => handleUpdateArticle('h2_4', ''))}>
              <EditableField
                tag="h2"
                value={art?.h2_4}
                onChange={val => handleUpdateArticle('h2_4', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p10', ''))}>
              <EditableField
                tag="p"
                value={art?.p10}
                onChange={val => handleUpdateArticle('p10', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p11', ''))}>
              <EditableField
                tag="p"
                value={art?.p11}
                onChange={val => handleUpdateArticle('p11', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p12', ''))}>
              <EditableField
                tag="p"
                value={art?.p12}
                onChange={val => handleUpdateArticle('p12', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>

            {(isEditMode || hasValue(art?.p13_strong) || hasValue(art?.p13_text)) && (
              <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Block?', 'Remove this bold text block?', () => { handleUpdateArticle('p13_strong', ''); handleUpdateArticle('p13_text', ''); })}>
                <div className={css.richParagraph}>
                  <EditableField
                    tag="strong"
                    value={art?.p13_strong}
                    onChange={val => handleUpdateArticle('p13_strong', val)}
                    isEditMode={isEditMode}
                  />{' '}
                  <EditableField
                    tag="span"
                    value={art?.p13_text}
                    onChange={val => handleUpdateArticle('p13_text', val)}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              </EditWrapper>
            )}
            {(isEditMode || hasValue(art?.p14_strong) || hasValue(art?.p14_text)) && (
              <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Block?', 'Remove this bold text block?', () => { handleUpdateArticle('p14_strong', ''); handleUpdateArticle('p14_text', ''); })}>
                <div className={css.richParagraph}>
                  <EditableField
                    tag="strong"
                    value={art?.p14_strong}
                    onChange={val => handleUpdateArticle('p14_strong', val)}
                    isEditMode={isEditMode}
                  />{' '}
                  <EditableField
                    tag="span"
                    value={art?.p14_text}
                    onChange={val => handleUpdateArticle('p14_text', val)}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              </EditWrapper>
            )}
            {(isEditMode || hasValue(art?.p15_strong) || hasValue(art?.p15_text)) && (
              <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Block?', 'Remove this bold text block?', () => { handleUpdateArticle('p15_strong', ''); handleUpdateArticle('p15_text', ''); })}>
                <div className={css.richParagraph}>
                  <EditableField
                    tag="strong"
                    value={art?.p15_strong}
                    onChange={val => handleUpdateArticle('p15_strong', val)}
                    isEditMode={isEditMode}
                  />{' '}
                  <EditableField
                    tag="span"
                    value={art?.p15_text}
                    onChange={val => handleUpdateArticle('p15_text', val)}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              </EditWrapper>
            )}

            <EngagementBox items={pageData?.stats} onChange={handleUpdate} isEditMode={isEditMode} triggerConfirm={triggerConfirm} />

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Heading?', 'Remove this heading?', () => handleUpdateArticle('h2_5', ''))}>
              <EditableField
                tag="h2"
                value={art?.h2_5}
                onChange={val => handleUpdateArticle('h2_5', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Sub-heading?', 'Remove this sub-heading?', () => handleUpdateArticle('h3_2', ''))}>
              <EditableField
                tag="p"
                className={css.h3}
                value={art?.h3_2}
                onChange={val => handleUpdateArticle('h3_2', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p16', ''))}>
              <EditableField
                tag="p"
                value={art?.p16}
                onChange={val => handleUpdateArticle('p16', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Sub-heading?', 'Remove this sub-heading?', () => handleUpdateArticle('h3_3', ''))}>
              <EditableField
                tag="p"
                className={css.h3}
                value={art?.h3_3}
                onChange={val => handleUpdateArticle('h3_3', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p17', ''))}>
              <EditableField
                tag="p"
                value={art?.p17}
                onChange={val => handleUpdateArticle('p17', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>

            {(isEditMode || hasValue(art?.quote2)) && (
              <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Quote?', 'Remove this pull quote?', () => handleUpdateArticle('quote2', ''))}>
                <div className={css.pullQuote}>
                  <EditableField
                    tag="p"
                    value={art?.quote2}
                    onChange={val => handleUpdateArticle('quote2', val)}
                    isEditMode={isEditMode}
                    multiline
                  />
                </div>
              </EditWrapper>
            )}

            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Heading?', 'Remove this heading?', () => handleUpdateArticle('h2_6', ''))}>
              <EditableField
                tag="h2"
                value={art?.h2_6}
                onChange={val => handleUpdateArticle('h2_6', val)}
                isEditMode={isEditMode}
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p18', ''))}>
              <EditableField
                tag="p"
                value={art?.p18}
                onChange={val => handleUpdateArticle('p18', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p19', ''))}>
              <EditableField
                tag="p"
                value={art?.p19}
                onChange={val => handleUpdateArticle('p19', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>
            <EditWrapper isEditMode={isEditMode} onDelete={() => triggerConfirm('Delete Paragraph?', 'Remove this paragraph?', () => handleUpdateArticle('p20', ''))}>
              <EditableField
                tag="p"
                value={art?.p20}
                onChange={val => handleUpdateArticle('p20', val)}
                isEditMode={isEditMode}
                multiline
              />
            </EditWrapper>

            <CtaBlock data={pageData?.cta} onChange={handleUpdate} isEditMode={isEditMode} triggerConfirm={triggerConfirm} />
          </div>
          </>
        );
    }
  };

  const blogTitle = stripHtml(pageData?.hero?.title);
  const blogDescription = stripHtml(pageData?.hero?.subtext);
  const blogImage = pageData?.hero?.image || pageData?.image;

  const pageTitle = blogTitle || 'The Forteh Blog';
  const pageDescription = blogDescription
    || 'Insights, tips, and trends about fractional work and the future of careers from Forteh.';

  const socialImage = blogImage || config.branding.facebookImage;
  const twitterImage = blogImage || config.branding.twitterImage;
  const canonicalUrl = `${config.marketplaceRootURL}/forteh-blog/${slug || ''}`;

  const articleSchema = blogTitle ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blogTitle,
    description: pageDescription,
    image: socialImage ? [socialImage] : [],
    author: {
      '@type': 'Person',
      name: 'Raman',
      jobTitle: 'Founder of Forteh',
    },
    publisher: {
      '@type': 'Organization',
      name: config.marketplaceName,
      logo: {
        '@type': 'ImageObject',
        url: config.branding.logoImageMobileURL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    url: canonicalUrl,
  } : null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: config.marketplaceRootURL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${config.marketplaceRootURL}/everything-fractional` },
      { '@type': 'ListItem', position: 3, name: blogTitle || 'Blog Post', item: canonicalUrl },
    ],
  };

  const schema = [breadcrumbSchema, ...(articleSchema ? [articleSchema] : [])];

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      scrollingDisabled={scrollingDisabled}
      openGraphType="article"
      schema={schema}
      facebookImages={[{ width: 1200, height: 630, url: socialImage }]}
      twitterImages={[{ width: 600, height: 314, url: twitterImage }]}
      socialSharing={{
        title: pageTitle,
        description: pageDescription,
        images1200: [{ width: 1200, height: 630, url: socialImage }],
        images600: [{ width: 600, height: 314, url: twitterImage }],
      }}
    >
      <LayoutSingleColumn
        topbar={
          <TopbarContainer
            className={css.topbar}
            mobileRootClassName={css.mobileTopbar}
            desktopClassName={css.desktopTopbar}
            currentPage="FortehBlogPage"
          />
        }
        footer={<FooterComponent />}
        className={css.pageRoot}
      >
        <div className={css.root}>
          {renderTemplate()}
          {/* Admin Controls */}
          {isAdmin && (
            <div className={css.adminControls}>
              <div className={css.statusIndicator}>
                Viewing: <strong>{currentStatus?.toUpperCase()}</strong>
              </div>
              <button
                className={classNames(css.adminBtn, css.editToggleBtn)}
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? 'Preview Mode' : 'Edit Mode'}
              </button>
              
              <button
                className={classNames(css.adminBtn, css.saveBtn)}
                onClick={() => handleSave('draft')}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>

              <button
                className={classNames(css.adminBtn, css.publishBtn)}
                onClick={handlePublish}
                disabled={saving}
              >
                {saving ? 'Updating...' : 'Update Live'}
              </button>
            </div>
          )}

          {saving && <div className={css.loadingOverlay}>Saving changes...</div>}

          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            title={confirmModal.title}
            message={confirmModal.message}
            onConfirm={confirmModal.onConfirm}
            onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          />

        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const FortehBlogPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(FortehBlogPageComponent);

export default FortehBlogPage;
